// src/pages/Dashboard/Dashboard.js
import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  alpha,
  useTheme,
  Tooltip,
  Fade
} from '@mui/material'
import {
  AddCircleOutlineRounded,
  HomeRounded,
  FavoriteRounded,
  EditRounded,
  DeleteRounded,
  VisibilityRounded,
  RocketLaunchRounded,
  TimerRounded,
  CheckCircleRounded,
  HourglassEmptyRounded
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { userAPI, propertiesAPI } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import PropertyCard from '../../components/PropertyCard/PropertyCard'
import BoostPlans from '../../components/BoostPlans/BoostPlans'
import BoostDashboard from '../../components/BoostDashboard/BoostDashboard'

const Dashboard = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { user } = useAuth()
  const [currentTab, setCurrentTab] = useState(0)
  const [userProperties, setUserProperties] = useState([])
  const [favorites, setFavorites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    property: null
  })
  const [boostDialog, setBoostDialog] = useState({
    open: false,
    property: null
  })

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const [propertiesResponse, favoritesResponse] = await Promise.all([
        userAPI.properties(),
        userAPI.favorites()
      ])

      setUserProperties(propertiesResponse.data)
      setFavorites(favoritesResponse.data)
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      setError('Erro ao carregar dados. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteProperty = async () => {
    try {
      await propertiesAPI.delete(deleteDialog.property.id)
      setUserProperties((prev) =>
        prev.filter((p) => p.id !== deleteDialog.property.id)
      )
      setDeleteDialog({ open: false, property: null })
    } catch (error) {
      console.error('Erro ao deletar:', error)
      setError('Erro ao deletar imóvel.')
    }
  }

  const handleFavoriteToggle = (propertyId) => {
    setFavorites((prev) => prev.filter((fav) => fav.id !== propertyId))
  }

  const handleBoostCreated = () => {
    loadDashboardData()
    setBoostDialog({ open: false, property: null })
  }

  const getBoostInfo = (property) => {
    const boost = property.active_boost
    if (!boost) return null

    if (boost.status === 'active' && boost.end_date) {
      const endDate = new Date(boost.end_date)
      const now = new Date()
      const diffMs = endDate - now
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

      if (diffDays <= 0) {
        return {
          label: 'Expirado',
          color: 'default',
          icon: <TimerRounded sx={{ fontSize: 14 }} />
        }
      }
      return {
        label: `${boost.plan?.name || 'Ativo'} - ${diffDays}d restantes`,
        color: 'success',
        icon: <CheckCircleRounded sx={{ fontSize: 14 }} />
      }
    }

    if (boost.status === 'pending') {
      return {
        label: 'Pgto. Pendente',
        color: 'warning',
        icon: <HourglassEmptyRounded sx={{ fontSize: 14 }} />
      }
    }

    return null
  }

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )

  if (loading) {
    return (
      <Container maxWidth='xl'>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth='xl'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Box>
          <Typography variant='h5' fontWeight='bold'>
            Dashboard
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Bem-vindo, {user?.username}
          </Typography>
        </Box>

        <Button
          variant='contained'
          size='small'
          startIcon={<AddCircleOutlineRounded />}
          onClick={() => navigate('/dashboard/novo-imovel')}
        >
          Novo Imóvel
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Cards de Estatísticas - Compactos */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 4 }}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Meus Imóveis
                  </Typography>
                  <Typography variant='h4' fontWeight='bold' color='primary'>
                    {userProperties.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <HomeRounded />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 4 }}>
          <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Favoritos
                  </Typography>
                  <Typography variant='h4' fontWeight='bold' color='error'>
                    {favorites.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                  <FavoriteRounded />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Imóveis Ativos
                  </Typography>
                  <Typography
                    variant='h4'
                    fontWeight='bold'
                    color='success.main'
                  >
                    {userProperties.filter((p) => p.is_active).length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                  <VisibilityRounded />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
          <Tab label={`Meus Imóveis (${userProperties.length})`} />
          <Tab label={`Favoritos (${favorites.length})`} />
          <Tab
            icon={<RocketLaunchRounded sx={{ fontSize: 16 }} />}
            iconPosition='start'
            label='Impulsionamentos'
          />
        </Tabs>
      </Box>

      {/* Meus Imóveis */}
      <TabPanel value={currentTab} index={0}>
        {userProperties.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <HomeRounded
                sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant='h6' color='text.secondary' gutterBottom>
                Nenhum imóvel cadastrado
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Comece adicionando seu primeiro imóvel
              </Typography>
              <Button
                variant='contained'
                startIcon={<AddCircleOutlineRounded />}
                onClick={() => navigate('/dashboard/novo-imovel')}
              >
                Adicionar Imóvel
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {userProperties.map((property) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}
                key={property.id}
              >
                <Box sx={{ position: 'relative' }}>
                  <PropertyCard
                    property={property}
                    onFavoriteToggle={() => {}}
                  />

                  {!property.is_active && (
                    <Chip
                      label='Inativo'
                      color='error'
                      size='small'
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        fontWeight: 'bold',
                        zIndex: 2
                      }}
                    />
                  )}

                  {property.approval_status === 'pending' && (
                    <Chip
                      label='Em avaliação'
                      size='small'
                      sx={{
                        position: 'absolute',
                        top: property.is_active ? 8 : 40,
                        left: 8,
                        fontWeight: 'bold',
                        zIndex: 2,
                        bgcolor: '#FF9800',
                        color: 'white'
                      }}
                    />
                  )}

                  {property.approval_status === 'rejected' && (
                    <Chip
                      label='Recusado'
                      size='small'
                      sx={{
                        position: 'absolute',
                        top: property.is_active ? 8 : 40,
                        left: 8,
                        fontWeight: 'bold',
                        zIndex: 2,
                        bgcolor: '#d32f2f',
                        color: 'white'
                      }}
                    />
                  )}

                  {/* Chip de status do boost */}
                  {getBoostInfo(property) && (
                    <Chip
                      icon={getBoostInfo(property).icon}
                      label={getBoostInfo(property).label}
                      color={getBoostInfo(property).color}
                      size='small'
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                        zIndex: 2,
                        '& .MuiChip-icon': { ml: 0.5 }
                      }}
                    />
                  )}

                  {/* Botões de ação */}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    mt: 1,
                    flexDirection: 'column'
                  }}
                >
                  {/* Botão IMPULSIONAR */}
                  {(!property.active_boost ||
                    property.active_boost.status !== 'active') &&
                  property.approval_status === 'approved' ? (
                    <Tooltip
                      title='Impulsionar Imóvel'
                      placement='top'
                      TransitionComponent={Fade}
                      arrow
                    >
                      <Button
                        variant='contained'
                        size='small'
                        fullWidth
                        startIcon={<RocketLaunchRounded />}
                        onClick={() => setBoostDialog({ open: true, property })}
                        sx={{
                          bgcolor: '#FF9800',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          py: 0.75,
                          boxShadow: 3,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          '&:hover': {
                            bgcolor: '#F57C00',
                            boxShadow: 4,
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Impulsionar
                      </Button>
                    </Tooltip>
                  ) : null}

                  {/* Botões de editar e excluir */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title='Editar' placement='top' arrow>
                      <IconButton
                        size='small'
                        sx={{
                          flex: 1,
                          bgcolor: 'rgba(255,255,255,0.95)',
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'white',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() =>
                          navigate(`/dashboard/editar-imovel/${property.id}`)
                        }
                      >
                        <EditRounded fontSize='small' color='primary' />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title='Excluir' placement='top' arrow>
                      <IconButton
                        size='small'
                        sx={{
                          flex: 1,
                          bgcolor: 'rgba(255,255,255,0.95)',
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'white',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() =>
                          setDeleteDialog({ open: true, property })
                        }
                      >
                        <DeleteRounded fontSize='small' color='error' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Favoritos */}
      <TabPanel value={currentTab} index={1}>
        {favorites.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <FavoriteRounded
                sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant='h6' color='text.secondary' gutterBottom>
                Nenhum favorito
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Explore os imóveis e favorite os que gostar
              </Typography>
              <Button variant='outlined' onClick={() => navigate('/imoveis')}>
                Buscar Imóveis
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {favorites.map((favorite) => (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={favorite.id}>
                <PropertyCard
                  property={favorite}
                  onFavoriteToggle={handleFavoriteToggle}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Impulsionamentos */}
      <TabPanel value={currentTab} index={2}>
        <BoostDashboard onBoostProperty={() => setCurrentTab(0)} />
      </TabPanel>

      {/* Dialog de Exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, property: null })}
        maxWidth='xs'
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Tem certeza que deseja excluir "{deleteDialog.property?.title}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, property: null })}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteProperty}
            color='error'
            variant='contained'
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Boost */}
      <BoostPlans
        open={boostDialog.open}
        onClose={() => setBoostDialog({ open: false, property: null })}
        property={boostDialog.property}
        onBoostCreated={handleBoostCreated}
      />
    </Container>
  )
}

export default Dashboard
